import { useState } from "react";
import {
  useProducts,
  useUpsertProduct,
  useDeleteProduct,
  type Product,
  type ProductDraft,
  type ProductUnit,
} from "@/lib/db";

const UNITS: { value: ProductUnit; label: string }[] = [
  { value: "stueck", label: "Stück" },
  { value: "glas", label: "Glas" },
  { value: "flasche", label: "Flasche" },
  { value: "beutel", label: "Beutel" },
  { value: "kg", label: "Kilogramm" },
  { value: "liter", label: "Liter" },
];

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

export default function ProduktePage() {
  const [editing, setEditing] = useState<Product | "new" | null>(null);
  const { data: products = [], isLoading, error } = useProducts();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-hof-900">Produkte</h1>
        <button onClick={() => setEditing("new")} className="btn-primary">+ Neues Produkt</button>
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800">
          {String((error as Error).message)}
        </div>
      )}

      {isLoading ? (
        <div className="card">Lade…</div>
      ) : products.length === 0 ? (
        <div className="card text-center text-hof-600">
          Noch keine Produkte angelegt. <button className="text-wiese-700 underline" onClick={() => setEditing("new")}>Erstes Produkt anlegen</button>
        </div>
      ) : (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-hof-50 text-sm text-hof-600">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Kategorie</th>
                <th className="p-3">Einheit</th>
                <th className="p-3 text-right">Preis</th>
                <th className="p-3">MwSt</th>
                <th className="p-3">Status</th>
                <th className="p-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-hof-100 hover:bg-hof-50/50">
                  <td className="p-3">
                    <div className="font-medium text-hof-900">{p.name}</div>
                    {p.sku && <div className="text-xs text-hof-500">SKU: {p.sku}</div>}
                  </td>
                  <td className="p-3 text-hof-700">{p.category ?? "—"}</td>
                  <td className="p-3 text-hof-700">{UNITS.find((u) => u.value === p.unit)?.label ?? p.unit}</td>
                  <td className="p-3 text-right font-mono">{fmt.format(p.price_cents / 100)}</td>
                  <td className="p-3 text-hof-700">{p.vat_rate}%</td>
                  <td className="p-3">
                    {p.is_active ? <span className="pill-success">aktiv</span> : <span className="pill-warn">inaktiv</span>}
                  </td>
                  <td className="p-3">
                    <button onClick={() => setEditing(p)} className="btn-ghost p-2" aria-label="Bearbeiten">✎</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ProduktDrawer
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ProduktDrawer({ initial, onClose }: { initial: Product | null; onClose: () => void }) {
  const upsert = useUpsertProduct();
  const del = useDeleteProduct();

  const [form, setForm] = useState<ProductDraft & { id?: string }>(() => ({
    id: initial?.id,
    name: initial?.name ?? "",
    sku: initial?.sku ?? null,
    description: initial?.description ?? null,
    unit: initial?.unit ?? "stueck",
    price_cents: initial?.price_cents ?? 0,
    vat_rate: 7, // fest — siehe Kommentar am MwSt-Feld weiter unten
    image_url: initial?.image_url ?? null,
    category: initial?.category ?? null,
    is_active: initial?.is_active ?? true,
    sort_order: initial?.sort_order ?? 0,
  }));
  const [priceStr, setPriceStr] = useState(((initial?.price_cents ?? 0) / 100).toFixed(2).replace(".", ","));
  const [err, setErr] = useState<string | null>(null);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const cents = Math.round(parseFloat(priceStr.replace(",", ".")) * 100);
    if (!Number.isFinite(cents) || cents < 0) return setErr("Preis ungültig");
    if (!form.name.trim()) return setErr("Name fehlt");
    try {
      await upsert.mutateAsync({ ...form, price_cents: cents });
      onClose();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  }

  async function onDelete() {
    if (!form.id) return;
    if (!confirm(`Produkt "${form.name}" wirklich löschen?`)) return;
    try {
      await del.mutateAsync(form.id);
      onClose();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-hof-900/40 flex justify-end" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSave}
        className="bg-hof-50 w-full max-w-md h-full overflow-y-auto p-6 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-hof-900">{initial ? "Produkt bearbeiten" : "Neues Produkt"}</h2>
          <button type="button" onClick={onClose} className="btn-ghost p-2" aria-label="Schließen">✕</button>
        </div>

        <div>
          <label className="label">Name *</label>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Preis (€) *</label>
            <input className="input" inputMode="decimal" value={priceStr} onChange={(e) => setPriceStr(e.target.value)} required />
          </div>
          {/* Fest auf 7 %, wie im neuen Produkte-Modul: Der Hof verkauft nur
              landwirtschaftliche Erzeugnisse. Diese Seite hier ist der ALTE
              Backoffice-Weg — sie ist über /backoffice noch erreichbar, und eine
              zugenagelte Tür neben einer offenen wäre keine. */}
          <div>
            <label className="label">MwSt %</label>
            <div className="input flex items-center text-hof-700">7 % — Erzeugnisse</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Einheit</label>
            <select className="input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value as ProductUnit })}>
              {UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Kategorie</label>
            <input className="input" value={form.category ?? ""}
              onChange={(e) => setForm({ ...form, category: e.target.value || null })}
              placeholder="z.B. Suppen, Eier" />
          </div>
        </div>

        <div>
          <label className="label">SKU (intern)</label>
          <input className="input" value={form.sku ?? ""}
            onChange={(e) => setForm({ ...form, sku: e.target.value || null })} />
        </div>

        <div>
          <label className="label">Beschreibung</label>
          <textarea className="input" rows={3} value={form.description ?? ""}
            onChange={(e) => setForm({ ...form, description: e.target.value || null })} />
        </div>

        <div>
          <label className="label">Bild-URL</label>
          <input className="input" value={form.image_url ?? ""}
            onChange={(e) => setForm({ ...form, image_url: e.target.value || null })}
            placeholder="https://..." />
        </div>

        <label className="flex items-center gap-3 min-h-touch cursor-pointer">
          <input type="checkbox" checked={form.is_active ?? true}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="w-5 h-5" />
          <span>Produkt aktiv im Shop</span>
        </label>

        {err && <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-sm">{err}</div>}

        <div className="flex gap-2 pt-2">
          <button type="submit" className="btn-primary flex-1" disabled={upsert.isPending}>
            {upsert.isPending ? "Speichern…" : "Speichern"}
          </button>
          {form.id && (
            <button type="button" onClick={onDelete} className="btn-danger" disabled={del.isPending}>
              Löschen
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
